"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import type { ListRelationship } from "@/types/list-relationship";
import { checkItemCompatibility } from "@/lib/connectors/field-compatibility";

interface ListItem {
  id: string;
  erp_id: string;
  data: Record<string, unknown>;
}

interface UseListRelationshipsResult {
  relationships: ListRelationship[];
  loading: boolean;
  /**
   * Check if a specific item is compatible with current parent selections.
   */
  checkCompatibility: (
    listSlug: string,
    itemData: Record<string, unknown>,
    parentSelections: Record<string, string>
  ) => "compatible" | "incompatible" | "unknown";
  /**
   * Split items into compatible and incompatible groups based on parent selections.
   * Compatible items are sorted first.
   */
  partitionItems: (
    items: ListItem[],
    listSlug: string,
    parentSelections: Record<string, string>
  ) => { compatible: ListItem[]; incompatible: ListItem[] };
}

export function useListRelationships(connectionId: string | undefined): UseListRelationshipsResult {
  const [relationships, setRelationships] = useState<ListRelationship[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!connectionId) {
      setRelationships([]);
      setLoading(false);
      return;
    }

    async function fetchRelationships() {
      try {
        const res = await fetch(`/api/erp-connections/${connectionId}`);
        if (!res.ok) {
          setRelationships([]);
          return;
        }
        const json = await res.json();
        const connection = json.data ?? json;
        setRelationships((connection.list_relationships ?? []) as ListRelationship[]);
      } catch {
        setRelationships([]);
      } finally {
        setLoading(false);
      }
    }

    fetchRelationships();
  }, [connectionId]);

  const checkCompatibility = useCallback(
    (listSlug: string, itemData: Record<string, unknown>, parentSelections: Record<string, string>) => {
      const rel = relationships.find(r => r.source_list === listSlug);
      if (!rel) return "compatible" as const;

      const parentValue = parentSelections[rel.target_list];
      return checkItemCompatibility(itemData, parentValue, rel);
    },
    [relationships]
  );

  const partitionItems = useCallback(
    (items: ListItem[], listSlug: string, parentSelections: Record<string, string>) => {
      const rel = relationships.find(r => r.source_list === listSlug);
      if (!rel) return { compatible: items, incompatible: [] };

      const parentValue = parentSelections[rel.target_list];
      if (!parentValue) return { compatible: items, incompatible: [] };

      const compatible: ListItem[] = [];
      const incompatible: ListItem[] = [];
      for (const item of items) {
        const result = checkItemCompatibility(item.data, parentValue, rel);
        if (result === "incompatible") {
          incompatible.push(item);
        } else {
          compatible.push(item);
        }
      }
      return { compatible, incompatible };
    },
    [relationships]
  );

  return useMemo(
    () => ({ relationships, loading, checkCompatibility, partitionItems }),
    [relationships, loading, checkCompatibility, partitionItems]
  );
}
